import { useState } from 'react'
import type { ReactNode } from 'react'
import { Notice, Tooltip } from './ui'
import type { PlayerSchedule, ScheduleFixture } from '../types'

/** Exact wording requested for the Copy Discord Tags hint, spacing included. */
const COPY_TAGS_TOOLTIP =
  'Clicking this button will copy your remaining opponents.  Paste this in discord and hit enter to tag them.'

/** Roster name that must not ping Discord on Tuesday. Matched case-insensitively. */
const DPS_DISPLAY_NAME = 'dps'

/** What replaces DPS's `<@id>` mention while the Tuesday window is open. */
const DPS_TUESDAY_PLACEHOLDER = "Don't tag DPS on Tuesday"

/**
 * League local time, matching the deadline clock in the API.
 *
 * @remarks
 * Named zone rather than UTC−6, because Central flips between CDT and CST and
 * a hardcoded offset would put the Tuesday window an hour off for half the year.
 */
const LEAGUE_TIME_ZONE = 'America/Chicago'

/**
 * Whether `at` falls on Tuesday between 12:01am and 11:59pm Central.
 *
 * @param at - Instant the copy button was pressed; defaults to now.
 * @returns True only inside that window, so Monday night and Wednesday morning
 * still produce a real mention.
 *
 * @remarks
 * The first minute of Tuesday (12:00am–12:00:59am) is left out on purpose:
 * the requested window starts at 12:01am. After 11:59pm the weekday is still
 * Tuesday until midnight, and that last minute is kept so the gag does not
 * drop a ping in the final seconds of the day.
 *
 * @example
 * ```typescript
 * isDpsQuietTuesday(new Date('2026-09-01T05:01:00Z')) // true: 12:01am CDT Tuesday
 * isDpsQuietTuesday(new Date('2026-09-02T04:59:00Z')) // false: 11:59pm CDT Monday
 * ```
 */
function isDpsQuietTuesday(at: Date = new Date()): boolean {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: LEAGUE_TIME_ZONE,
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(at)

  const weekday = parts.find(part => part.type === 'weekday')?.value
  const hour = Number(parts.find(part => part.type === 'hour')?.value)
  const minute = Number(parts.find(part => part.type === 'minute')?.value)
  if (weekday !== 'Tue' || !Number.isFinite(hour) || !Number.isFinite(minute)) {
    return false
  }

  const minutes = hour * 60 + minute
  return minutes >= 1 && minutes <= 23 * 60 + 59
}

function isDpsOpponent(fixture: ScheduleFixture): boolean {
  return fixture.opponentDisplayName.trim().toLowerCase() === DPS_DISPLAY_NAME
}

/**
 * Builds a paste-ready Discord message that mentions each remaining opponent.
 *
 * @param fixtures - Unplayed fixtures from the schedule endpoint.
 * @param at - Instant the button was pressed, so a Tuesday check cannot drift
 * from a render that happened hours earlier.
 * @returns A space-separated line of `<@id>` mentions; anyone without a linked
 * Discord account contributes their display name as plain text instead, so the
 * message never contains a broken `<@null>` mention. On Tuesday in Central
 * time, DPS is replaced with {@link DPS_TUESDAY_PLACEHOLDER} rather than pinged.
 *
 * @example
 * ```typescript
 * formatDiscordTags([
 *   { opponentDisplayName: 'Ada', opponentDiscordId: '123', ... },
 *   { opponentDisplayName: 'Grace', opponentDiscordId: null, ... },
 * ]) // '<@123> Grace'
 *
 * formatDiscordTags(
 *   [{ opponentDisplayName: 'DPS', opponentDiscordId: '999', ... }],
 *   new Date('2026-09-01T17:00:00Z'),
 * ) // "Don't tag DPS on Tuesday"
 * ```
 */
function formatDiscordTags(fixtures: ScheduleFixture[], at: Date = new Date()): string {
  const hushDps = isDpsQuietTuesday(at)
  return fixtures
    .map(fixture => {
      if (hushDps && isDpsOpponent(fixture)) {
        return DPS_TUESDAY_PLACEHOLDER
      }
      return fixture.opponentDiscordId
        ? `<@${fixture.opponentDiscordId}>`
        : fixture.opponentDisplayName
    })
    .join(' ')
}

/** Async Clipboard API with a hidden-textarea fallback for insecure contexts. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Denied permissions and non-HTTPS origins both land here; try the old way.
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()

  try {
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    document.body.removeChild(textarea)
  }
}

interface CopyState {
  tone: 'success' | 'error'
  message: string
}

/**
 * Action row whose Copy Discord Tags button puts the signed-in player's
 * remaining opponents on the clipboard as Discord mentions.
 *
 * @param schedule - An already-fetched schedule for the season on show. Pass
 * the raw result rather than a filtered list: the server's
 * `unavailableReason` is what tells an unrostered player why the button is
 * dead, and refetching here would duplicate a request the page has already
 * made. Null or undefined while the page is still loading.
 * @param loading - Keeps the button disabled until `schedule` is trustworthy,
 * so an early click cannot copy an empty line.
 * @param children - Extra actions rendered before the copy button, sharing its
 * row. The hero passes its own links through so its button stays inline with
 * them, while a page that needs the button alone passes nothing.
 *
 * @remarks
 * Callers own the empty and signed-out states; render this only once there is
 * a session, otherwise the button appears with nothing it can copy.
 *
 * The rendered fragment is a row followed by its explanation and copy
 * feedback, so it inherits the spacing of whichever column it is dropped into.
 *
 * The copy payload is built at click time. If that instant is Tuesday between
 * 12:01am and 11:59pm Central and DPS is among the remaining opponents, his
 * mention is replaced with "Don't tag DPS on Tuesday" so a paste cannot ping
 * him.
 *
 * @example
 * ```tsx
 * const schedule = useAsync(() => fetchMySchedule(seasonNumber), [seasonNumber])
 * <CopyDiscordTags schedule={schedule.data} loading={schedule.loading} />
 * ```
 */
export function CopyDiscordTags({
  schedule,
  loading = false,
  children,
}: {
  schedule: PlayerSchedule | null | undefined
  loading?: boolean
  children?: ReactNode
}) {
  const [copyState, setCopyState] = useState<CopyState | null>(null)

  const unplayed = (schedule?.fixtures ?? []).filter(fixture => !fixture.played)
  const blockedReason =
    schedule?.unavailableReason ??
    (schedule && unplayed.length === 0
      ? 'You have already played everyone in your conference.'
      : null)
  const canCopy = unplayed.length > 0 && !blockedReason

  const handleCopy = async (): Promise<void> => {
    const copied = await copyToClipboard(formatDiscordTags(unplayed))
    setCopyState(
      copied
        ? {
            tone: 'success',
            message: `Copied ${unplayed.length} ${
              unplayed.length === 1 ? 'opponent' : 'opponents'
            }. Paste into Discord and hit enter to tag them.`,
          }
        : { tone: 'error', message: 'Could not reach the clipboard. Copy manually instead.' }
    )
  }

  return (
    <>
      <div className="row">
        {children}
        <Tooltip text={COPY_TAGS_TOOLTIP}>
          <button
            type="button"
            className="btn"
            disabled={!canCopy || loading}
            onClick={() => void handleCopy()}
          >
            Copy Discord Tags
          </button>
        </Tooltip>
      </div>

      {blockedReason && <p className="muted">{blockedReason}</p>}
      {copyState && <Notice tone={copyState.tone}>{copyState.message}</Notice>}
    </>
  )
}
